import React, { useEffect, useState, useContext, Suspense, lazy } from 'react';
import { AuthProvider } from "./AuthContext";
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import ReactPixel from 'react-facebook-pixel';
import { SettingsContext } from './SettingsContext';
import { API_BASE_URL } from './Api';

import ScrollToTop from './Components/ScrollToTop';
import MirrorLoader from './Components/MirrorLoader';
import BottomNav from './Components/BottomNav';
import { CartProvider } from "./CartContext";

// Lazy loaded page components for optimal speed and bundle size
const Home = lazy(() => import('./Pages/Home'));
const Ghee = lazy(() => import('./Pages/Ghee'));
const Laddu = lazy(() => import('./Pages/Laddu'));
const Contact = lazy(() => import('./Pages/Contact'));
const DesiCowGhee = lazy(() => import('./Pages/DesiCowGhee'));
const OurStory = lazy(() => import('./Pages/OurStory'));
const TrackOrder = lazy(() => import('./Pages/TrackOrder'));
const OrdersDetail = lazy(() => import('./Pages/OrdersDetail'));
const Reviewus = lazy(() => import('./Pages/Reviewus'));
const Login = lazy(() => import('./Pages/Login'));
const Register = lazy(() => import('./Pages/Register'));
const Cart = lazy(() => import('./Pages/Cart'));
const ProductCarousel = lazy(() => import('./Pages/ProductCarousel'));
const Ourpolicies = lazy(() => import('./Pages/Ourpolicies'));
const Privacypolicies = lazy(() => import('./Pages/privacy-policy'));
const Checkout = lazy(() => import('./Pages/Checkout'));
const Blog = lazy(() => import('./Pages/Blog'));
const ArticleCard = lazy(() => import('./Pages/ArticleCard'));
const OurProducts = lazy(() => import('./Pages/OurProducts'));
const CategoryProduct = lazy(() => import('./Pages/CategoryProduct'));
const AllProducts = lazy(() => import('./Pages/AllProducts'));
const Returnpolicies = lazy(() => import('./Pages/return-policy'));
const Shippingpolicies = lazy(() => import('./Pages/shipping-policy'));
const TermsConditions = lazy(() => import('./Pages/terms-conditions'));
const Orderpop = lazy(() => import('./Pages/Orderpop'));
const ProfilePage = lazy(() => import('./Pages/ProfilePage'));
const MyOrders = lazy(() => import('./Pages/MyOrders'));
const Complaint = lazy(() => import('./Pages/Complaint'));
const NotFound = lazy(() => import('./Pages/NotFound'));
const NotServiceable = lazy(() => import('./Pages/NotServiceable'));
const FranchiseForm = lazy(() => import('./Pages/FranchiseForm'));
const OrderSuccess = lazy(() => import('./Pages/OrderSuccess'));
const OrderFailed = lazy(() => import('./Pages/OrderFailed'));
const AdLandingPage = lazy(() => import('./Pages/AdLandingPage'));

function App() {
  const user = JSON.parse(localStorage.getItem("user"));
  const [isBlocked, setIsBlocked] = useState(false);
  const [locationChecked, setLocationChecked] = useState(false);
  const { settings } = useContext(SettingsContext);
  const [prevShiprocket, setPrevShiprocket] = useState(null);

  useEffect(() => {
    if (settings) {
      const currentShiprocket = !!settings.web_shiprocket;
      if (prevShiprocket !== null && prevShiprocket !== currentShiprocket) {
        window.location.reload();
      } else {
        setPrevShiprocket(currentShiprocket);
      }
    }
  }, [settings, prevShiprocket]);

  useEffect(() => {
    if (settings?.web_shiprocket) {
      const isStaging = settings.shiprocket_api_key && (settings.shiprocket_api_key.includes('dev') || settings.shiprocket_api_key.includes('test'));
      
      // Load CSS
      const link = document.createElement("link");
      link.id = "shiprocket-checkout-css";
      link.rel = "stylesheet";
      link.href = isStaging
        ? "https://customcheckoutfastrr.netlify.app/assets/styles/shopify.css"
        : "https://checkout-ui.shiprocket.com/assets/styles/shopify.css";
      document.head.appendChild(link);

      // Load JS
      const script = document.createElement("script");
      script.id = "shiprocket-checkout-js";
      script.src = isStaging
        ? "https://customcheckoutfastrr.netlify.app/assets/js/channels/shopify.js"
        : "https://checkout-ui.shiprocket.com/assets/js/channels/shopify.js";
      script.async = true;
      document.body.appendChild(script);

      return () => {
        const existingLink = document.getElementById("shiprocket-checkout-css");
        if (existingLink) existingLink.remove();
        const existingScript = document.getElementById("shiprocket-checkout-js");
        if (existingScript) existingScript.remove();
      };
    }
  }, [settings]);

  useEffect(() => {
    const pixelOptions = {
      autoConfig: true,
      debug: false,
    };

    if (typeof window !== "undefined" && !window.fbqInitialized) {
      ReactPixel.init(process.env.REACT_APP_FACEBOOK_PIXEL_ID || "419304844367207", pixelOptions);
      window.fbqInitialized = true;
    }
  }, []);

  useEffect(() => {
    const detectLocation = async () => {
      const logLocationWarning = (...args) => {
        if (process.env.NODE_ENV !== "production") {
          console.warn(...args);
        }
      };

      // 1. Check session cache first
      try {
        const cachedStatus = sessionStorage.getItem('location_blocked');
        if (cachedStatus !== null) {
          if (cachedStatus === 'true') {
            setIsBlocked(true);
          }
          setLocationChecked(true);
          return;
        }
      } catch (e) {
        logLocationWarning('sessionStorage is not accessible:', e);
      }

      let blocked = false;
      let data = null;

      // 2. Try primary geolocation service (our system IP detector)
      try {
        const baseUrl = API_BASE_URL || "http://127.0.0.1:8000/api";
        const response = await fetch(`${baseUrl}/detect-country`);
        if (response.ok) {
          data = await response.json();
        } else {
          logLocationWarning(`System IP detector failed with status: ${response.status}. Trying fallback...`);
        }
      } catch (error) {
        logLocationWarning('System IP detector fetch failed, trying fallback...', error);
      }

      // 3. Fallback to ipapi.co if primary failed
      if (!data) {
        try {
          const response = await fetch('https://ipapi.co/json/');
          if (response.ok) {
            data = await response.json();
          } else {
            logLocationWarning(`Fallback ipapi.co failed with status: ${response.status}`);
          }
        } catch (error) {
          logLocationWarning('Fallback ipapi.co fetch failed:', error);
        }
      }

      // 4. Perform check if we successfully retrieved geolocation data
      if (data) {
        const city   = (data.city   || '').toLowerCase();
        const region = (data.region || '').toLowerCase();
        const org    = (data.org    || '').toLowerCase();
        const postal = (data.postal || '');

        if (
          city.includes('sikar') || 
          region.includes('sikar') ||
          org.includes('sikar') ||
          postal.startsWith('332')
        ) {
          blocked = true;
        }
      }

      // 5. Cache result and update state
      try {
        if (data) {
          sessionStorage.setItem('location_data', JSON.stringify(data));
        }
        sessionStorage.setItem('location_blocked', blocked ? 'true' : 'false');
      } catch (e) {
        logLocationWarning('Failed to cache location status:', e);
      }

      if (blocked) {
        setIsBlocked(true);
      }
      setLocationChecked(true);
    };

    detectLocation();
  }, []);

  if (isBlocked) {
    return <NotServiceable />;
  }

  return (
    <AuthProvider>
      <div className="app-container">
        <div id="redirect-text-fastrr" style={{ display: 'none', visibility: 'hidden' }}></div>
        <CartProvider>
          <BrowserRouter>
            {!locationChecked && <MirrorLoader type="page" />}
            <ScrollToTop />
            <Suspense fallback={<MirrorLoader type="page" />}>
              <Routes>
                {/* pages */}
                <Route path='/' element={<Home />} />
                <Route path='/home' element={<Home />} />
                <Route path='/ghee' element={<Ghee />} />
                <Route path='/laddu' element={<Laddu />} />
                <Route path='/contact' element={<Contact />} />
                <Route path='/desi-cow-ghee' element={<DesiCowGhee />} />
                <Route path='/our-story' element={<OurStory />} />
                <Route path='/track-order' element={<TrackOrder />} />
                <Route path='/orders/:id' element={<OrdersDetail />} />
                <Route path='/reviewus' element={<Reviewus />} />
                <Route path='/login' element={<Login />} />
                <Route path='/register' element={<Register />} />
                <Route path='/cart' element={<Cart />} />
                <Route path='/product/:slug' element={<ProductCarousel />} />
                <Route path='/our-policies' element={<Ourpolicies />} />
                <Route path='/privacy-policy' element={<Privacypolicies />} />
                <Route path='/checkout' element={<Checkout />} />
                <Route path='/blog' element={<Blog />} />
                <Route path='/blogs/:slug' element={<ArticleCard />} />
                <Route path='/our-products' element={<OurProducts />} />
                <Route path="/category/:slug" element={<CategoryProduct />} />
                <Route path="/all-products" element={<AllProducts />} />
                <Route path="/return-policy" element={<Returnpolicies />} />
                <Route path="/shipping-policy" element={<Shippingpolicies />} />
                <Route path="/terms-conditions" element={<TermsConditions />} />
                <Route path="/orderpop" element={<Orderpop />} />
                <Route path="/my-profile" element={<ProfilePage user={user} />} />
                <Route path="/my-orders" element={<MyOrders user={user} />} />
                <Route path="/complaint" element={<Complaint user={user} />} />
                <Route path="/franchise" element={<FranchiseForm />} />
                <Route path="/success" element={<OrderSuccess />} />
                <Route path="/failed" element={<OrderFailed />} />
                <Route path="/not-serviceable" element={<NotServiceable />} />
                <Route path="/:slug" element={<AdLandingPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <BottomNav />
          </BrowserRouter>
        </CartProvider>
      </div>
    </AuthProvider>
  );
}

export default App;



